import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface PayslipDispute {
  id: string;
  payroll_record_id: string;
  profile_id: string;
  organization_id: string;
  pay_period: string;
  dispute_category: string;
  description: string;
  status: string;
  manager_reviewed_at: string | null;
  manager_reviewed_by: string | null;
  manager_notes: string | null;
  hr_reviewed_at: string | null;
  hr_reviewed_by: string | null;
  hr_notes: string | null;
  finance_reviewed_at: string | null;
  finance_reviewed_by: string | null;
  finance_notes: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  revised_payroll_record_id: string | null;
  created_at: string;
  updated_at: string;
  // joined
  profiles?: { full_name: string | null; email: string | null; department: string | null } | null;
}

const DISPUTE_CATEGORIES = [
  { value: "salary_mismatch", label: "Salary Mismatch" },
  { value: "deduction_error", label: "Incorrect Deduction" },
  { value: "allowance_missing", label: "Missing Allowance" },
  { value: "tax_error", label: "Tax Calculation Error" },
  { value: "ot_missing", label: "Overtime Not Reflected" },
  { value: "other", label: "Other" },
] as const;

export { DISPUTE_CATEGORIES };

/** Employee: view my own disputes */
export function useMyPayslipDisputes() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my-payslip-disputes", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!profile) return [];

      const { data, error } = await supabase
        .from("payslip_disputes")
        .select("*")
        .eq("profile_id", profile.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as PayslipDispute[];
    },
    enabled: !!user,
  });
}

/** Employee: raise a dispute */
export function useRaisePayslipDispute() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      payroll_record_id: string;
      pay_period: string;
      dispute_category: string;
      description: string;
    }) => {
      if (!user) throw new Error("Not authenticated");
      if (!input.description?.trim()) throw new Error("Dispute description is required");

      // Validate category
      const validCategories: string[] = DISPUTE_CATEGORIES.map(c => c.value);
      if (!validCategories.includes(input.dispute_category)) {
        throw new Error("Invalid dispute category");
      }

      // Prevent duplicate active disputes for the same payroll record
      const { data: existing } = await supabase
        .from("payslip_disputes")
        .select("id")
        .eq("payroll_record_id", input.payroll_record_id)
        .not("status", "in", '("rejected","approved")')
        .limit(1);
      if (existing && existing.length > 0) {
        throw new Error("An active dispute already exists for this payslip. Please wait for it to be resolved.");
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();
      if (!profile) throw new Error("Profile not found");

      // Fetch the user's organization_id (required by RLS)
      const { data: orgData } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", profile.id)
        .single();
      if (!orgData?.organization_id) throw new Error("Organization not found");

      const { data, error } = await supabase
        .from("payslip_disputes")
        .insert({
          payroll_record_id: input.payroll_record_id,
          profile_id: profile.id,
          organization_id: orgData.organization_id,
          pay_period: input.pay_period,
          dispute_category: input.dispute_category,
          description: input.description,
          status: "pending_manager",
        } as any)
        .select("*")
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: orgData.organization_id,
        actor_id: user.id,
        action: "payslip_dispute_raised",
        entity_type: "payslip_dispute",
        entity_id: (data as any)?.id ?? null,
        target_user_id: profile.id,
        metadata: {
          pay_period: input.pay_period,
          dispute_category: input.dispute_category,
          payroll_record_id: input.payroll_record_id,
        },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-payslip-disputes"] });
      toast.success("Payslip dispute raised successfully. Your manager will review it.");
    },
    onError: (err: any) => toast.error("Failed to raise dispute: " + err.message),
  });
}

/** Manager/HR/Finance: get pending disputes for review */
export function usePendingPayslipDisputes(role: "manager" | "hr" | "finance" | null) {
  const { user } = useAuth();
  const statusMap = {
    manager: "pending_manager",
    hr: "pending_hr",
    finance: "pending_finance",
  } as const;

  return useQuery({
    queryKey: ["payslip-disputes-pending", role, user?.id],
    queryFn: async () => {
      if (!user || !role) return [];

      // For manager role, only fetch disputes from direct reports (exclude own)
      if (role === "manager") {
        const { data: myProfile } = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", user.id)
          .single();
        if (!myProfile) return [];

        const { data: reports } = await supabase
          .from("profiles")
          .select("id")
          .eq("manager_id", myProfile.id);
        const reportIds = (reports || []).map((r) => r.id);
        if (reportIds.length === 0) return [];

        const { data, error } = await supabase
          .from("payslip_disputes")
          .select("*, profiles:profile_id(full_name, email, department)")
          .eq("status", statusMap[role])
          .in("profile_id", reportIds)
          .order("created_at", { ascending: false });
        if (error) throw error;
        return (data || []) as unknown as PayslipDispute[];
      }

      // HR/Finance: fetch all disputes with the relevant status
      const { data, error } = await supabase
        .from("payslip_disputes")
        .select("*, profiles:profile_id(full_name, email, department)")
        .eq("status", statusMap[role])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as PayslipDispute[];
    },
    enabled: !!user && !!role,
    staleTime: 60_000, // 1 min — disputes are infrequent
  });
}

/** Manager: forward to HR or reject */
export function useManagerReviewDispute() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ disputeId, action, notes }: { disputeId: string; action: "forward" | "reject"; notes?: string }) => {
      if (!user) throw new Error("Not authenticated");

      // Resolve caller org for tenant isolation
      const { data: callerProfile } = await supabase.from("profiles").select("organization_id").eq("user_id", user.id).maybeSingle();
      if (!callerProfile?.organization_id) throw new Error("Organization not found");

      // Double-review guard
      const { data: check } = await supabase
        .from("payslip_disputes")
        .select("status, profile_id")
        .eq("id", disputeId)
        .single();
      if ((check as any)?.status !== "pending_manager") {
        throw new Error("This dispute has already been reviewed or is no longer pending manager review");
      }
      // Self-review guard: manager cannot approve a dispute for themselves
      // Compare via profiles to map profile_id → user_id
      const { data: disputantProfile } = await supabase.from("profiles").select("user_id").eq("id", (check as any)?.profile_id).maybeSingle();
      if (disputantProfile?.user_id === user.id) {
        throw new Error("You cannot review your own payslip dispute.");
      }

      const update: any = {
        manager_reviewed_at: new Date().toISOString(),
        manager_reviewed_by: user.id,
        manager_notes: notes || null,
        updated_at: new Date().toISOString(),
      };
      if (action === "forward") {
        update.status = "pending_hr";
      } else {
        update.status = "rejected";
        update.resolved_at = new Date().toISOString();
        update.resolution_notes = notes || "Rejected by manager";
      }
      const { error } = await supabase
        .from("payslip_disputes")
        .update(update)
        .eq("id", disputeId)
        .eq("organization_id", callerProfile.organization_id);
      if (error) throw error;
      return { disputeId, action, notes };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["payslip-disputes-pending"] });
      queryClient.invalidateQueries({ queryKey: ["my-payslip-disputes"] });
      toast.success(result.action === "forward" ? "Dispute forwarded to HR" : "Dispute rejected");

      // Send notification & email
      supabase.functions.invoke("send-notification-email", {
        body: {
          type: "payslip_dispute_reviewed",
          payload: {
            dispute_id: result.disputeId,
            decision: result.action === "forward" ? "forwarded_to_hr" : "rejected",
            reviewer_name: user?.user_metadata?.full_name ?? user?.email ?? "Manager",
            reviewer_notes: result.notes || null,
          },
        },
      }).catch((err) => console.warn("Failed to send dispute review notification:", err));
    },
    onError: (err: any) => toast.error(err.message),
  });
}

/** HR: forward to Finance or reject */
export function useHRReviewDispute() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ disputeId, action, notes }: { disputeId: string; action: "forward" | "reject"; notes?: string }) => {
      if (!user) throw new Error("Not authenticated");
      // Resolve caller org for tenant isolation
      const { data: callerProfile } = await supabase.from("profiles").select("organization_id").eq("user_id", user.id).maybeSingle();
      if (!callerProfile?.organization_id) throw new Error("Organization not found");

      const update: any = {
        hr_reviewed_at: new Date().toISOString(),
        hr_reviewed_by: user.id,
        hr_notes: notes || null,
        updated_at: new Date().toISOString(),
      };
      if (action === "forward") {
        update.status = "pending_finance";
      } else {
        update.status = "rejected";
        update.resolved_at = new Date().toISOString();
        update.resolution_notes = notes || "Rejected by HR";
      }
      const { error } = await supabase
        .from("payslip_disputes")
        .update(update)
        .eq("id", disputeId)
        .eq("organization_id", callerProfile.organization_id);
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: callerProfile.organization_id,
        actor_id: user.id,
        action: `payslip_dispute_hr_${action}`,
        entity_type: "payslip_dispute",
        entity_id: disputeId,
        metadata: {
          before_state: { status: "pending_manager" },
          after_state: { status: update.status },
          notes: notes || null,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payslip-disputes-pending"] });
      toast.success("Dispute reviewed successfully");
    },
    onError: (err: any) => toast.error(err.message),
  });
}

/** Finance: approve (auto-apply correction) or reject */
export function useFinanceReviewDispute() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ disputeId, action, notes }: { disputeId: string; action: "approve" | "reject"; notes?: string }) => {
      if (!user) throw new Error("Not authenticated");
      // Resolve caller org for tenant isolation
      const { data: callerProfile } = await supabase.from("profiles").select("organization_id").eq("user_id", user.id).maybeSingle();
      if (!callerProfile?.organization_id) throw new Error("Organization not found");

      const update: any = {
        finance_reviewed_at: new Date().toISOString(),
        finance_reviewed_by: user.id,
        finance_notes: notes || null,
        updated_at: new Date().toISOString(),
      };
      if (action === "approve") {
        update.status = "approved";
        update.resolved_at = new Date().toISOString();
        update.resolution_notes = notes || "Approved by Finance — payslip correction applied automatically";
      } else {
        update.status = "rejected";
        update.resolved_at = new Date().toISOString();
        update.resolution_notes = notes || "Rejected by Finance";
      }
      const { error } = await supabase
        .from("payslip_disputes")
        .update(update)
        .eq("id", disputeId)
        .eq("organization_id", callerProfile.organization_id);
      if (error) throw error;

      // On approval, mark the old payroll record as superseded to enable revision
      if (action === "approve") {
        // Fetch the dispute to get payroll_record_id
        const { data: dispute } = await supabase
          .from("payslip_disputes")
          .select("payroll_record_id, profile_id, pay_period")
          .eq("id", disputeId)
          .single();
        const disputeData = dispute as { payroll_record_id: string; profile_id: string; pay_period: string } | null;
        if (disputeData?.payroll_record_id) {
          // Mark existing record as superseded — set BOTH fields so the
          // engine fallback query (is_superseded=false) correctly excludes it.
          await supabase
            .from("payroll_records")
            .update({
              status: "superseded",
              is_superseded: true,
              updated_at: new Date().toISOString(),
            } as any)
            .eq("id", disputeData.payroll_record_id)
            .eq("organization_id", callerProfile.organization_id);

          // GBC-139: post a GL reversal for the superseded entry so the books
          // unwind cleanly before HR/Finance posts the corrected entry.
          // When the revised payslip is later generated and its entry id is
          // known, callers re-invoke with p_corrected_entry_id filled in.
          type PayslipReversalRpc = (
            fn: "create_payslip_reversal_entry",
            args: { p_original_entry_id: string; p_corrected_entry_id: string | null; p_dispute_id: string }
          ) => Promise<{ data: null; error: unknown }>;
          const { error: revErr } = await (supabase.rpc as unknown as PayslipReversalRpc)(
            "create_payslip_reversal_entry",
            {
              p_original_entry_id: disputeData.payroll_record_id,
              p_corrected_entry_id: null,
              p_dispute_id: disputeId,
            }
          );
          if (revErr) throw revErr;
        }
      }

      await supabase.from("audit_logs").insert({
        organization_id: callerProfile.organization_id,
        actor_id: user.id,
        action: `payslip_dispute_finance_${action}`,
        entity_type: "payslip_dispute",
        entity_id: disputeId,
        metadata: {
          before_state: { status: "pending_finance" },
          after_state: { status: update.status },
          notes: notes || null,
        },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["payslip-disputes-pending"] });
      queryClient.invalidateQueries({ queryKey: ["payroll-records"] });
      queryClient.invalidateQueries({ queryKey: ["approved-dispute"] });
      toast.success(
        variables.action === "approve"
          ? "Dispute approved — payslip marked for revision. HR/Finance can now generate a corrected payslip."
          : "Dispute rejected"
      );
    },
    onError: (err: any) => toast.error(err.message),
  });
}

/** Check if a payslip dispute is approved (enables editing) for a given payroll record */
export function useHasApprovedDispute(payrollRecordId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["approved-dispute", payrollRecordId],
    queryFn: async () => {
      if (!payrollRecordId) return false;
      const { data, error } = await supabase
        .from("payslip_disputes")
        .select("id")
        .eq("payroll_record_id", payrollRecordId)
        .eq("status", "approved")
        .is("revised_payroll_record_id", null)
        .limit(1);
      if (error) return false;
      return (data?.length ?? 0) > 0;
    },
    enabled: !!user && !!payrollRecordId,
  });
}
